import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { IndianRupee, Users as UsersIcon, Package, Pencil, Trash2, MapPin, Camera, Filter, RefreshCw } from "lucide-react";
import { formatINR, formatNum } from "@/lib/format";
import { getUser } from "@/lib/auth";

const KIND_DEFS = {
  sales:       { label: "Sales",       icon: IndianRupee, editable: ["glasses_sold", "damaged_glasses", "cash_amount", "upi_amount", "card_amount", "opening_glass_stock", "remarks"] },
  attendance:  { label: "Attendance",  icon: UsersIcon,   editable: ["status", "check_out_time"] },
  consumables: { label: "Stock",       icon: Package,     editable: ["opening_stock", "closing_stock", "consumption", "item"] },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function EditDialog({ open, onOpenChange, kind, row, onSaved }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (row) {
      const fields = KIND_DEFS[kind].editable;
      const v = {};
      fields.forEach((f) => { v[f] = row[f] ?? ""; });
      setValues(v);
    }
  }, [row, kind]);

  if (!row) return null;
  const save = async () => {
    setSaving(true);
    try {
      // Coerce numerics
      const patch = {};
      Object.entries(values).forEach(([k, v]) => {
        if (["glasses_sold", "damaged_glasses", "cash_amount", "upi_amount", "card_amount",
             "opening_glass_stock", "opening_stock", "closing_stock", "consumption"].includes(k)) {
          patch[k] = Number(v) || 0;
        } else {
          patch[k] = v;
        }
      });
      const { data } = await api.patch(`/records/${kind}/${row.id}`, patch);
      toast.success(`Updated. ${Object.keys(data.diff || {}).length} field(s) changed.`);
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="daily-edit-dialog" className="rounded-2xl max-h-[90vh] overflow-y-auto max-w-lg">
        <DialogHeader><DialogTitle>Edit {KIND_DEFS[kind].label.toLowerCase()} entry</DialogTitle></DialogHeader>
        <div className="text-xs text-gray-500 -mt-2 mb-2">
          {row.store_name} · {row.date} · by {row.promoter_name}
        </div>
        <div className="space-y-3">
          {KIND_DEFS[kind].editable.map((f) => (
            <div key={f}>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">{f.replace(/_/g, " ")}</label>
              <Input
                data-testid={`edit-${f}`}
                value={values[f] ?? ""}
                onChange={(e) => setValues({ ...values, [f]: e.target.value })}
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="save-daily-edit-btn" disabled={saving} onClick={save} className="bg-brand-red hover:bg-[#B31721] text-white">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ row, kind, onOpenChange }) {
  if (!row) return null;
  return (
    <Dialog open={!!row} onOpenChange={onOpenChange}>
      <DialogContent data-testid="daily-detail-dialog" className="rounded-2xl max-h-[90vh] overflow-y-auto max-w-lg">
        <DialogHeader><DialogTitle>{KIND_DEFS[kind].label} entry detail</DialogTitle></DialogHeader>
        <div className="text-xs text-gray-500 -mt-2 mb-2">
          {row.store_name} · {row.date} · Submitted {fmtDateTime(row.submitted_at || row.check_in_time || row.created_at)} by {row.promoter_name}
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {Object.entries(row).map(([k, v]) => {
            if (["_id", "id", "selfie_photo", "project_id"].includes(k)) return null;
            const val = typeof v === "object" ? JSON.stringify(v) : String(v ?? "—");
            if (val.length > 200) return null;
            return (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-widest text-gray-500">{k.replace(/_/g, " ")}</div>
                <div className="font-medium break-words">{val}</div>
              </div>
            );
          })}
        </div>
        {row.selfie_photo && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 flex items-center gap-1"><Camera className="h-3 w-3" /> Selfie</div>
            <img src={row.selfie_photo} alt="selfie" className="rounded-lg border border-gray-200 max-h-64" />
          </div>
        )}
        {(row.gps_lat && row.gps_lng) && (
          <div className="mt-2 text-xs text-gray-600 flex items-center gap-1">
            <MapPin className="h-3 w-3" /> GPS: {row.gps_lat.toFixed(5)}, {row.gps_lng.toFixed(5)} · Distance: {row.distance_m ?? "—"}m
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDailyEntries() {
  const user = getUser();
  const [kind, setKind] = useState("sales");
  const [facets, setFacets] = useState({ stores: [], promoters: [] });
  const [storeId, setStoreId] = useState("");
  const [promoterId, setPromoterId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editRow, setEditRow] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const loadFacets = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/daily-entries/facets");
      setFacets(data);
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { kind, limit: 200 };
      if (storeId) params.store_id = storeId;
      if (promoterId) params.promoter_id = promoterId;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const { data } = await api.get("/admin/daily-entries", { params });
      setRows(data.rows);
      setTotal(data.total);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, [kind, storeId, promoterId, fromDate, toDate]);

  useEffect(() => { loadFacets(); }, [loadFacets]);
  useEffect(() => { load(); }, [load]);

  const clearFilters = () => { setStoreId(""); setPromoterId(""); setFromDate(""); setToDate(""); };

  const performDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/records/${kind}/${confirmDel.id}?hard=true`);
      toast.success("Entry permanently deleted. Traces remain only in the Audit Log.");
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const salesColumns = (r) => (
    <>
      <td className="px-3 py-2 text-right font-semibold" data-testid={`row-cups-${r.id}`}>{formatNum(r.glasses_sold)}</td>
      <td className="px-3 py-2 text-right">{formatINR(r.cash_amount)}</td>
      <td className="px-3 py-2 text-right">{formatINR(r.upi_amount)}</td>
      <td className="px-3 py-2 text-right">{formatINR(r.card_amount || 0)}</td>
      <td className="px-3 py-2 text-right font-bold text-brand-red">{formatINR(r.total_sales)}</td>
      <td className="px-3 py-2">
        {r.is_late_entry && <Badge className="bg-amber-50 text-amber-800 border border-amber-200 text-[10px]">LATE</Badge>}
        {r.payment_check_flag && <Badge className="bg-red-50 text-brand-red border border-red-200 text-[10px] ml-1">MISMATCH</Badge>}
      </td>
    </>
  );
  const attendanceColumns = (r) => (
    <>
      <td className="px-3 py-2">
        <Badge className={r.status === "Present" ? "bg-green-50 text-green-800 border border-green-200" :
          r.status === "Late" ? "bg-amber-50 text-amber-800 border border-amber-200" :
          "bg-red-50 text-brand-red border border-red-200"}>{r.status}</Badge>
      </td>
      <td className="px-3 py-2 text-xs">{fmtDateTime(r.sign_in_time || r.check_in_time)}</td>
      <td className="px-3 py-2 text-xs">
        {fmtDateTime(r.sign_out_time || r.check_out_time) || <span className="text-gray-400">—</span>}
        {r.sign_out_kind === "auto_midnight" && (
          <div className="text-[10px] text-amber-700">auto sign-out · day end</div>
        )}
      </td>
      <td className="px-3 py-2 text-right text-xs">
        {r.out_of_geofence ? <span className="text-brand-red font-semibold">Out {r.distance_m}m</span> : <span className="text-green-700">In {r.distance_m ?? "—"}m</span>}
      </td>
      <td className="px-3 py-2">
        {r.selfie_photo && <Camera className="h-4 w-4 text-brand-red" />}
        {r.is_late_entry && <Badge className="bg-amber-50 text-amber-800 border border-amber-200 text-[10px] ml-1">LATE</Badge>}
      </td>
    </>
  );
  const consumablesColumns = (r) => (
    <>
      <td className="px-3 py-2 font-semibold">{r.item}</td>
      <td className="px-3 py-2 text-right">{formatNum(r.opening_stock || 0)}</td>
      <td className="px-3 py-2 text-right">{formatNum(r.closing_stock || 0)}</td>
      <td className="px-3 py-2 text-right font-bold">{formatNum(r.consumption || 0)}</td>
      <td className="px-3 py-2 text-xs text-gray-500">{r.remarks || "—"}</td>
    </>
  );

  const KindIcon = KIND_DEFS[kind].icon;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="Daily Entries" subtitle="View · Edit · Permanently Delete staff-submitted records" />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">
          <Card className="p-5 rounded-xl border-gray-200 bg-white">
            <div className="flex items-center gap-2 mb-3">
              <KindIcon className="h-5 w-5 text-brand-red" />
              <div className="font-display text-xl font-bold text-gray-900">{KIND_DEFS[kind].label} entries</div>
              <span className="text-xs text-gray-500">· {total} total</span>
            </div>
            <Tabs value={kind} onValueChange={(v) => setKind(v)}>
              <TabsList className="bg-gray-100 rounded-lg p-1" data-testid="daily-entries-kind-tabs">
                <TabsTrigger value="sales" data-testid="tab-daily-sales" className="data-[state=active]:bg-white data-[state=active]:text-brand-red rounded-md text-xs px-3">Sales</TabsTrigger>
                <TabsTrigger value="attendance" data-testid="tab-daily-attendance" className="data-[state=active]:bg-white data-[state=active]:text-brand-red rounded-md text-xs px-3">Attendance</TabsTrigger>
                <TabsTrigger value="consumables" data-testid="tab-daily-stock" className="data-[state=active]:bg-white data-[state=active]:text-brand-red rounded-md text-xs px-3">Stock</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1 flex items-center gap-1"><Filter className="h-3 w-3" /> Store</div>
                <Select value={storeId || "all"} onValueChange={(v) => setStoreId(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-store" className="h-10"><SelectValue placeholder="All stores" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All stores</SelectItem>
                    {facets.stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.store_code} · {s.store_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1">Promoter</div>
                <Select value={promoterId || "all"} onValueChange={(v) => setPromoterId(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-promoter" className="h-10"><SelectValue placeholder="All promoters" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All promoters</SelectItem>
                    {facets.promoters.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1">From</div>
                <Input type="date" data-testid="filter-from-date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-10" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1">To</div>
                <Input type="date" data-testid="filter-to-date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-10" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn" className="h-10 flex-1">Clear</Button>
                <Button variant="outline" onClick={load} data-testid="reload-btn" className="h-10"><RefreshCw className="h-4 w-4" /></Button>
              </div>
            </div>
          </Card>

          <Card className="rounded-xl border-gray-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="daily-entries-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Store</th>
                    <th className="text-left px-3 py-2">Promoter</th>
                    {kind === "sales" && <>
                      <th className="text-right px-3 py-2">Cups</th>
                      <th className="text-right px-3 py-2">Cash</th>
                      <th className="text-right px-3 py-2">UPI</th>
                      <th className="text-right px-3 py-2">Card</th>
                      <th className="text-right px-3 py-2">Total</th>
                      <th className="text-left px-3 py-2">Flags</th>
                    </>}
                    {kind === "attendance" && <>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">Check-in</th>
                      <th className="text-left px-3 py-2">Check-out</th>
                      <th className="text-right px-3 py-2">Geo</th>
                      <th className="text-left px-3 py-2">Flags</th>
                    </>}
                    {kind === "consumables" && <>
                      <th className="text-left px-3 py-2">Item</th>
                      <th className="text-right px-3 py-2">Opening</th>
                      <th className="text-right px-3 py-2">Closing</th>
                      <th className="text-right px-3 py-2">Consumed</th>
                      <th className="text-left px-3 py-2">Remarks</th>
                    </>}
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={12} className="p-8 text-center text-gray-500">Loading…</td></tr>}
                  {!loading && rows.length === 0 && <tr><td colSpan={12} className="p-8 text-center text-gray-500">No entries match your filters.</td></tr>}
                  {!loading && rows.map((r) => (
                    <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`row-${kind}-${r.id}`}>
                      <td className="px-3 py-2 text-xs">{r.date}</td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-xs">{r.store_name}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">{r.promoter_name}</td>
                      {kind === "sales" && salesColumns(r)}
                      {kind === "attendance" && attendanceColumns(r)}
                      {kind === "consumables" && consumablesColumns(r)}
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setDetailRow(r)} title="View detail"
                            data-testid={`view-${kind}-${r.id}`}
                            className="h-8 w-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 text-[10px]">
                            View
                          </button>
                          <button onClick={() => setEditRow(r)} title="Edit"
                            data-testid={`edit-${kind}-${r.id}`}
                            className="h-8 w-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600">
                            <Pencil className="h-4 w-4" />
                          </button>
                          {user?.role === "Admin" && (
                            <button onClick={() => setConfirmDel(r)} title="Permanently delete"
                              data-testid={`delete-${kind}-${r.id}`}
                              className="h-8 w-8 rounded-lg hover:bg-red-50 flex items-center justify-center text-red-600">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="text-[11px] text-gray-500">
            Deletes here are <span className="font-bold text-red-700">permanent</span> — the entry vanishes from every dashboard, report and total instantly.
            The only trace kept is a single audit-log entry (who deleted, old values, when).
          </div>
        </main>
      </div>

      <EditDialog open={!!editRow} onOpenChange={(v) => !v && setEditRow(null)} kind={kind} row={editRow} onSaved={load} />
      <DetailDialog row={detailRow} kind={kind} onOpenChange={(v) => !v && setDetailRow(null)} />

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent data-testid="daily-del-dialog" className="rounded-2xl">
          <DialogHeader><DialogTitle>Permanently delete this entry?</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-700 space-y-2">
            <div>
              This will remove the {KIND_DEFS[kind].label.toLowerCase()} entry for <span className="font-semibold">{confirmDel?.store_name}</span> on <span className="font-semibold">{confirmDel?.date}</span> by <span className="font-semibold">{confirmDel?.promoter_name}</span>.
            </div>
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-800">
              Every dashboard, KPI and report will recalculate <span className="font-bold">immediately</span> as if this entry never existed. The only trace kept is a single line in the Audit Log.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button data-testid="confirm-daily-del-btn" onClick={performDelete} className="bg-red-600 hover:bg-red-700 text-white">
              <Trash2 className="h-4 w-4 mr-1.5" /> Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
